'use client';

import { useState, useCallback } from 'react';
import { BuilderForm } from './builder-form';
import { BuilderPreview } from './builder-preview';
import { BuilderState, DEFAULT_BUILDER_STATE } from './types';
import {
  loadDraft,
  saveDraft,
  clearDraft,
  isDraftEnabled,
} from './use-builder-storage';
import { useCookieConsent } from '@/hooks/useCookieConsent';

interface InvoiceBuilderSectionProps {
  locale: string;
  t: (key: string) => string;
  getAnimationClass: (id: string, type?: string) => string;
}

export function InvoiceBuilderSection({
  locale,
  t,
  getAnimationClass,
}: InvoiceBuilderSectionProps) {
  const { status: cookieStatus } = useCookieConsent();
  const cookiesAccepted = cookieStatus === 'accepted';

  const [{ state, saveDraftEnabled }, setDraftState] = useState(() => {
    if (typeof window !== 'undefined' && isDraftEnabled()) {
      const draft = loadDraft();
      if (draft) {
        return { state: draft, saveDraftEnabled: true };
      }
    }
    return { state: DEFAULT_BUILDER_STATE, saveDraftEnabled: false };
  });

  function setState(next: BuilderState) {
    setDraftState((prev) => ({ ...prev, state: next }));
  }

  function setSaveDraftEnabled(enabled: boolean) {
    setDraftState((prev) => ({ ...prev, saveDraftEnabled: enabled }));
  }

  const handleChange = useCallback(
    (next: BuilderState) => {
      setState(next);
      if (saveDraftEnabled && cookiesAccepted) {
        saveDraft(next);
      }
    },
    [saveDraftEnabled, cookiesAccepted]
  );

  function handleSaveDraftToggle(enabled: boolean) {
    if (!cookiesAccepted) return;
    setSaveDraftEnabled(enabled);
    if (enabled) {
      saveDraft(state);
    } else {
      clearDraft();
    }
  }

  return (
    <div className="py-16 px-5" id="invoice-builder" data-animate>
      <div
        className={`max-w-7xl mx-auto space-y-10 ${getAnimationClass('invoice-builder', 'fade-up')}`}
      >
        {/* Section header */}
        <div className="text-center space-y-3">
          <span className="text-sm font-semibold tracking-wide uppercase text-brand">
            {t('sectionLabel')}
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
            {t('title')}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t('subtitle')}
          </p>
        </div>

        {/* Builder layout: form left, preview right */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start bg-card rounded-xl ghost-border p-4 md:p-8">
          {/* Left: form */}
          <div>
            <BuilderForm
              state={state}
              onChange={handleChange}
              saveDraftEnabled={saveDraftEnabled}
              onSaveDraftToggle={handleSaveDraftToggle}
              cookiesAccepted={cookiesAccepted}
              t={t}
            />
          </div>

          {/* Right: preview (sticky on desktop) */}
          <div className="lg:sticky lg:top-24">
            <BuilderPreview state={state} locale={locale} t={t} />
          </div>
        </div>
      </div>
    </div>
  );
}
